import React, { useState, useEffect } from "react";
import { FaPlus, FaTrash, FaEdit } from "react-icons/fa";
import { getSingleCampaignTask, postSingleCampaignTask, updateTask } from "../services/Campaigns.ts";
import { useTheme } from "../contexts/ThemeContext.tsx";
import { getTheme, ThemeColors, ThemeName } from '../theme.ts';
import Toast from '../components/common/Toast.tsx';

interface Task {
  id: string;
  brief: string[];
  requirements: string[];
  guidelines: string[];
  payment_details: string[];
  captions: string[];
  campaign: string;
  created_at?: string;
  updated_at?: string;
  is_deleted?: boolean;
}

const Tasks: React.FC<{ id: string }> = ({ id }) => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName as ThemeName);

  const rawData = localStorage.getItem('user');
  let cred = rawData ? JSON.parse(rawData) : null;

  const [tasks, setTasks] = useState<Task[]>([]);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info'; show: boolean }>({ message: '', type: 'info', show: false });
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  
  // State for managing input fields
  const [fieldItems, setFieldItems] = useState({
    brief: [] as string[],
    requirements: [] as string[],
    guidelines: [] as string[],
    payment_details: [] as string[],
    captions: [] as string[],
  });
  
  // State for current inputs
  const [currentInputs, setCurrentInputs] = useState({
    brief: "",
    requirements: "",
    guidelines: "",
    payment_details: "",
    captions: "",
  });

  const handleCurrentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInputs({ ...currentInputs, [e.target.name]: e.target.value });
  };

  const getAllTasks = async () => {
    try {
      const response = await getSingleCampaignTask(id);
      setTasks(response?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getAllTasks();
  }, [id]);

  const handleAddItem = (field: keyof typeof fieldItems) => {
    if (currentInputs[field].trim() === "") return;
    
    setFieldItems(prev => ({
      ...prev,
      [field]: [...prev[field], currentInputs[field].trim()]
    }));
    
    setCurrentInputs(prev => ({ ...prev, [field]: "" }));
  };

  const handleRemoveItem = (field: keyof typeof fieldItems, index: number) => {
    setFieldItems(prev => {
      const newItems = [...prev[field]];
      newItems.splice(index, 1);
      return { ...prev, [field]: newItems };
    });
  };

  const handleAddTask = async () => {
    // Validation: Check if at least one field has items
    const hasItems = Object.values(fieldItems).some(arr => arr.length > 0);
    if (!hasItems) {
      setInvalidFields(new Set(['brief', 'requirements', 'guidelines', 'payment_details', 'captions']));
      setToast({ message: "Please add at least one item to any field before submitting.", type: 'error', show: true });
      return;
    }

    const taskData = {
      brief: fieldItems.brief,
      requirements: fieldItems.requirements,
      guidelines: fieldItems.guidelines,
      payment_details: fieldItems.payment_details,
      captions: fieldItems.captions,
      campaign: id,
    };

    const formData = new FormData();
    formData.append("brief", JSON.stringify(taskData.brief));
    formData.append("requirements", JSON.stringify(taskData.requirements));
    formData.append("guidelines", JSON.stringify(taskData.guidelines));
    formData.append("payment_details", JSON.stringify(taskData.payment_details));
    formData.append("captions", JSON.stringify(taskData.captions));
    formData.append("campaign", taskData.campaign);

    try {
      let response;
      if (isEditMode && editingTaskId) {
        // Update existing task
        response = await updateTask(editingTaskId, formData);
        if (response?.status === 200) {
          setToast({ message: "Task updated successfully!", type: 'success', show: true });
        }
      } else {
        // Create new task
        response = await postSingleCampaignTask(formData);
        if (response?.status === 201) {
          setToast({ message: "Task added successfully!", type: 'success', show: true });
        }
      }
      
      if (response) {
        getAllTasks();
        // Reset form state
        setFieldItems({
          brief: [],
          requirements: [],
          guidelines: [],
          payment_details: [],
          captions: [],
        });
        setCurrentInputs({
          brief: "",
          requirements: "",
          guidelines: "",
          payment_details: "",
          captions: "",
        });
        setInvalidFields(new Set());
        setIsTaskModalOpen(false);
        setIsEditMode(false);
        setEditingTaskId(null);
      }
    } catch (error) {
      console.error("Error saving task:", error);
      const action = isEditMode ? "update" : "add";
      setToast({ message: `Failed to ${action} task. Please try again.`, type: 'error', show: true });
    }
  };

  const handleEditTask = (task: Task) => {
    setIsEditMode(true);
    setEditingTaskId(task.id);
    setFieldItems({
      brief: task.brief || [],
      requirements: task.requirements || [],
      guidelines: task.guidelines || [],
      payment_details: task.payment_details || [],
      captions: task.captions || [],
    });
    setCurrentInputs({
      brief: "",
      requirements: "",
      guidelines: "",
      payment_details: "",
      captions: "",
    });
    setIsTaskModalOpen(true);
  };

  const handleCancelEdit = () => {
    setIsTaskModalOpen(false);
    setIsEditMode(false);
    setEditingTaskId(null);
    setFieldItems({
      brief: [],
      requirements: [],
      guidelines: [],
      payment_details: [],
      captions: [],
    });
    setCurrentInputs({
      brief: "",
      requirements: "",
      guidelines: "",
      payment_details: "",
      captions: "",
    });
  };

  // Reset form when modal is opened for adding new task
  useEffect(() => {
    if (isTaskModalOpen && !isEditMode) {
      setFieldItems({
        brief: [],
        requirements: [],
        guidelines: [],
        payment_details: [],
        captions: [],
      });
      setCurrentInputs({
        brief: "",
        requirements: "",
        guidelines: "",
        payment_details: "",
        captions: "",
      });
    }
  }, [isTaskModalOpen, isEditMode]);

  return (
    <>
      <div className="mt-6 shadow-lg rounded-lg p-6" style={{ backgroundColor: currentTheme.card, borderColor: currentTheme.border }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold" style={{ color: currentTheme.primary }}>Tasks</h3>
          {cred?.user?.role !== 'ADMIN' && (
            <button
              onClick={() => setIsTaskModalOpen(true)}
              className="px-4 py-2 rounded flex items-center gap-2 text-white transition-colors"
              style={{backgroundColor: currentTheme.primary}}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary || currentTheme.primary}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.primary}
            >
              <FaPlus /> Add Task
            </button>
          )}
        </div>

        {tasks?.length > 0 ? (
          <ul className="space-y-4">
            {tasks.map((task) => (
              <li key={task.id} className="p-4 border-b border-gray-200 last:border-b-0 relative">
                {cred?.user?.role !== 'ADMIN' && (
                  <button
                    onClick={() => handleEditTask(task)}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                    style={{ color: currentTheme.primary }}
                    title="Edit Task"
                  >
                    <FaEdit size={16} />
                  </button>
                )}
                
                <div className="mb-3">
                  <h4 className="font-semibold" style={{ color: currentTheme.primary }}>Brief:</h4>
                  {task.brief && task.brief.length > 0 ? (
                    <ul className="list-disc list-inside ml-4 mt-1" style={{ color: currentTheme.text }}>
                      {task.brief.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="ml-4" style={{ color: currentTheme.text }}>No brief provided</p>
                  )}
                </div>

                <div className="mb-3">
                  <h4 className="font-semibold" style={{ color: currentTheme.primary }}>Requirements:</h4>
                  {task.requirements && task.requirements.length > 0 ? (
                    <ul className="list-disc list-inside ml-4 mt-1" style={{ color: currentTheme.text }}>
                      {task.requirements.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="ml-4" style={{ color: currentTheme.text }}>No requirements provided</p>
                  )}
                </div>

                <div className="mb-3">
                  <h4 className="font-semibold" style={{ color: currentTheme.primary }}>Guidelines:</h4>
                  {task.guidelines && task.guidelines.length > 0 ? (
                    <ul className="list-disc list-inside ml-4 mt-1" style={{ color: currentTheme.text }}>
                      {task.guidelines.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="ml-4" style={{ color: currentTheme.text }}>No guidelines provided</p>
                  )}
                </div>

                <div className="mb-3">
                  <h4 className="font-semibold" style={{ color: currentTheme.primary }}>Payment Details:</h4>
                  {task.payment_details && task.payment_details.length > 0 ? (
                    <ul className="list-disc list-inside ml-4 mt-1" style={{ color: currentTheme.text }}>
                      {task.payment_details.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="ml-4" style={{ color: currentTheme.text }}>No payment details provided</p>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold" style={{ color: currentTheme.primary }}>Captions:</h4>
                  {task.captions && task.captions.length > 0 ? (
                    <ul className="list-disc list-inside ml-4 mt-1" style={{ color: currentTheme.text }}>
                      {task.captions.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="ml-4" style={{ color: currentTheme.text }}>No captions provided</p>
                  )}
                </div>

                {/* {task.created_at && (
                  <div className="mt-3 text-xs text-gray-500">
                    Created: {new Date(task.created_at).toLocaleDateString()}
                    {task.updated_at && ` | Updated: ${new Date(task.updated_at).toLocaleDateString()}`}
                  </div>
                )} */}
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-4 text-center" style={{ color: currentTheme.text }}>No tasks added yet.</p>
        )}

        {isTaskModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="rounded-lg shadow-xl w-full max-w-2xl border-2 max-h-[90vh] flex flex-col" style={{ backgroundColor: currentTheme.card, borderColor: currentTheme.border }}>
              <div className="p-4 border-b" style={{ borderColor: currentTheme.border }}>
                <h3 className="text-xl font-bold" style={{ color: currentTheme.primary }}>
                  {isEditMode ? "Edit Task" : "Add New Task"}
                </h3>
              </div>

              <div className="p-4 overflow-y-auto flex-grow">
                <div className="grid grid-cols-1 gap-4">
                  {/* Brief Section */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                      Brief
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="brief"
                        value={currentInputs.brief}
                        onChange={handleCurrentInputChange}
                        placeholder="Enter brief item"
                        className={`flex-grow p-2 border rounded text-sm focus:ring-2 ${invalidFields.has('brief') ? 'border-red-500' : ''}`}
                        style={{
                          backgroundColor: currentTheme.surface,
                          color: currentTheme.text,
                          borderColor: invalidFields.has('brief') ? '#ef4444' : currentTheme.border,
                          caretColor: currentTheme.text,
                        }}
                      />
                      <button
                        onClick={() => handleAddItem("brief")}
                        className="px-3 rounded flex items-center gap-1 text-white"
                        style={{
                          backgroundColor: currentTheme.primary,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary || currentTheme.primary}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.primary}
                      >
                        <FaPlus size={12} />
                      </button>
                    </div>
                    {fieldItems.brief.length > 0 && (
                      <div className="mt-2 border border-gray-200 rounded p-2 max-h-32 overflow-y-auto">
                        <ul className="space-y-1">
                          {fieldItems.brief.map((item, idx) => (
                            <li key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                              <span className="text-sm">{item}</span>
                              <button
                                onClick={() => handleRemoveItem("brief", idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FaTrash size={14} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Requirements Section */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                      Requirements
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="requirements"
                        value={currentInputs.requirements}
                        onChange={handleCurrentInputChange}
                        placeholder="Enter requirement"
                        className={`flex-grow p-2 border rounded text-sm focus:ring-2 ${invalidFields.has('requirements') ? 'border-red-500' : ''}`}
                        style={{
                          backgroundColor: currentTheme.surface,
                          color: currentTheme.text,
                          borderColor: invalidFields.has('requirements') ? '#ef4444' : currentTheme.border,
                          caretColor: currentTheme.text,
                        }}
                      />
                      <button
                        onClick={() => handleAddItem("requirements")}
                        className="px-3 rounded flex items-center gap-1 text-white"
                        style={{
                          backgroundColor: currentTheme.primary,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary || currentTheme.primary}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.primary}
                      >
                        <FaPlus size={12} />
                      </button>
                    </div>
                    {fieldItems.requirements.length > 0 && (
                      <div className="mt-2 border border-gray-200 rounded p-2 max-h-32 overflow-y-auto">
                        <ul className="space-y-1">
                          {fieldItems.requirements.map((item, idx) => (
                            <li key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                              <span className="text-sm">{item}</span>
                              <button
                                onClick={() => handleRemoveItem("requirements", idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FaTrash size={14} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Guidelines Section */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                      Guidelines
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="guidelines"
                        value={currentInputs.guidelines}
                        onChange={handleCurrentInputChange}
                        placeholder="Enter guideline"
                        className="flex-grow p-2 border rounded text-sm focus:ring-2"
                        style={{
                          backgroundColor: currentTheme.surface,
                          color: currentTheme.text,
                          borderColor: currentTheme.border,
                          caretColor: currentTheme.text,
                        }}
                      />
                      <button
                        onClick={() => handleAddItem("guidelines")}
                        className="px-3 rounded flex items-center gap-1 text-white"
                        style={{
                          backgroundColor: currentTheme.primary,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary || currentTheme.primary}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.primary}
                      >
                        <FaPlus size={12} />
                      </button>
                    </div>
                    {fieldItems.guidelines.length > 0 && (
                      <div className="mt-2 border border-gray-200 rounded p-2 max-h-32 overflow-y-auto">
                        <ul className="space-y-1">
                          {fieldItems.guidelines.map((item, idx) => (
                            <li key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                              <span className="text-sm">{item}</span>
                              <button
                                onClick={() => handleRemoveItem("guidelines", idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FaTrash size={14} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Payment Details Section */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                      Payment Details
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="payment_details"
                        value={currentInputs.payment_details}
                        onChange={handleCurrentInputChange}
                        placeholder="Enter payment detail"
                        className="flex-grow p-2 border rounded text-sm focus:ring-2"
                        style={{
                          backgroundColor: currentTheme.surface,
                          color: currentTheme.text,
                          borderColor: currentTheme.border,
                          caretColor: currentTheme.text,
                        }}
                      />
                      <button
                        onClick={() => handleAddItem("payment_details")}
                        className="px-3 rounded flex items-center gap-1 text-white"
                        style={{
                          backgroundColor: currentTheme.primary,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary || currentTheme.primary}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.primary}
                      >
                        <FaPlus size={12} />
                      </button>
                    </div>
                    {fieldItems.payment_details.length > 0 && (
                      <div className="mt-2 border border-gray-200 rounded p-2 max-h-32 overflow-y-auto">
                        <ul className="space-y-1">
                          {fieldItems.payment_details.map((item, idx) => (
                            <li key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                              <span className="text-sm">{item}</span>
                              <button
                                onClick={() => handleRemoveItem("payment_details", idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FaTrash size={14} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Captions Section */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                      Captions
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="captions"
                        value={currentInputs.captions}
                        onChange={handleCurrentInputChange}
                        placeholder="Enter caption"
                        className="flex-grow p-2 border rounded text-sm focus:ring-2"
                        style={{
                          backgroundColor: currentTheme.surface,
                          color: currentTheme.text,
                          borderColor: currentTheme.border,
                          caretColor: currentTheme.text,
                        }}
                      />
                      <button
                        onClick={() => handleAddItem("captions")}
                        className="px-3 rounded flex items-center gap-1 text-white"
                        style={{
                          backgroundColor: currentTheme.primary,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary || currentTheme.primary}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.primary}
                      >
                        <FaPlus size={12} />
                      </button>
                    </div>
                    {fieldItems.captions.length > 0 && (
                      <div className="mt-2 border border-gray-200 rounded p-2 max-h-32 overflow-y-auto">
                        <ul className="space-y-1">
                          {fieldItems.captions.map((item, idx) => (
                            <li key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                              <span className="text-sm">{item}</span>
                              <button
                                onClick={() => handleRemoveItem("captions", idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <FaTrash size={14} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Campaign ID */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                      Campaign ID
                    </label>
                    <div
                      className="w-full p-2 border rounded text-sm"
                      style={{
                        backgroundColor: currentTheme.surface,
                        color: currentTheme.text,
                        borderColor: currentTheme.border,
                      }}
                    >
                      {id}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 flex justify-end space-x-2">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTask}
                  className="px-4 py-2 text-white rounded text-sm transition-colors"
                  style={{
                    backgroundColor: currentTheme.primary,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary || currentTheme.primary}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.primary}
                >
                  {isEditMode ? "Update Task" : "Add Task"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </>
  );
};

export default Tasks;